import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { addRootClasses, removeRootClasses } from '../store/actions/UI';
import useAriaDisclosure from './useAriaDisclosure';
import { useTrapFocus } from './useTrapFocus';

interface UsePopupOptions {
  defaultIsOpen?: boolean;
  label?: string;
  closeOnOutsideClick?: boolean;
  closeOnEscape?: boolean;
  closeOnRouteChange?: boolean;
  restoreFocusOnClose?: boolean;
  onClose?: () => void
}

export default function usePopup<T extends HTMLElement = HTMLDivElement>(
  popupId: string,
  options?: UsePopupOptions,
) {
  const {
    defaultIsOpen,
    label,
    closeOnOutsideClick = true,
    closeOnEscape,
    closeOnRouteChange = true,
    restoreFocusOnClose,
    onClose,
  } = options || {};

  const lastFocusedElement = useRef<HTMLElement>();

  const dispatch = useDispatch();
  const location = useLocation();

  const {
    isOpen, targetRef, toggleIsOpen, ...ariaDisclosure
  } = useAriaDisclosure<T>(
    popupId,
    {
      defaultIsOpen,
      closeOnOutsideClick: closeOnOutsideClick
        ? 'targetOnly'
        : false,
      closeOnEscape,
    },
  );

  useTrapFocus({
    ref: targetRef,
    isActive: isOpen,
  });

  /**
   * This use effect makes sure to always remove the 'modal-close'
   * and 'modal-open' root classNames when the popup is disposed,
   * to prevent unwanted layout changes.
   */
  useEffect(() => () => {
    dispatch(removeRootClasses('modal-open'));
    dispatch(removeRootClasses('modal-closed'));
  }, []);

  useEffect(() => {
    if (isOpen) {
      dispatch(addRootClasses('modal-open'));
      dispatch(removeRootClasses('modal-closed'));
    } else {
      dispatch(addRootClasses('modal-closed'));
      dispatch(removeRootClasses('modal-open'));

      if (onClose) {
        onClose();
      }
    }
  }, [isOpen]);

  /**
   * Sets focus on the popup so that screen readers announce
   * that the user is currently on a dialog.
   */
  useEffect(() => {
    if (isOpen && targetRef.current) {
      targetRef.current.setAttribute('tabindex', '-1');
      targetRef.current.focus();
    }

    function removeFocusProperties() {
      if (targetRef.current) {
        targetRef.current.removeAttribute('tabindex');
      }
    }

    if (targetRef.current) {
      targetRef.current.addEventListener('blur', removeFocusProperties);
    }

    return () => {
      if (targetRef.current) {
        targetRef.current.removeEventListener('blur', removeFocusProperties);
      }
    };
  }, [isOpen]);

  /**
   * Idempotent helpers wrapping the toggleIsOpen
   * callback, only to execute if an open/close
   * condition is satisfied
   */
  const open = useCallback(() => {
    if (!isOpen) {
      lastFocusedElement.current = document.activeElement as HTMLElement;
      toggleIsOpen();
    }
  }, [isOpen, toggleIsOpen]);

  const close = useCallback(() => {
    if (isOpen) {
      toggleIsOpen();
    }

    if (isOpen && restoreFocusOnClose) {
      lastFocusedElement.current?.focus();
    }
  }, [isOpen, toggleIsOpen]);

  useEffect(() => {
    if (closeOnRouteChange) {
      close();
    }
  }, [location]);

  const popupProps = {
    'aria-modal': true,
    role: 'dialog',
    id: popupId,
    ref: targetRef,
    onClose: close,
    ...(label
      ? {
        'aria-label': label,
      }
      : {}),
  };

  const toggleProps = {
    ...ariaDisclosure.toggleProps,
    onClick: toggleIsOpen,
  };

  return {
    isOpen,
    toggleIsOpen,
    open,
    close,
    toggleProps,
    popupProps,
  } as const;
}
